import {
    MapPin,
    Phone,
    Mail,
    ArrowUpRight,
} from "lucide-react";
import Seo from "@/components/layout/Seo";
import Reveal from "@/components/layout/Reveal";
import SectionCTA from "@/components/marketing/SectionCTA";
import MapEmbed from "@/components/marketing/MapEmbed";
import PrestationsCrossLinks from "@/components/marketing/PrestationsCrossLinks";
import ContactForm from "@/components/marketing/ContactForm";
import { SITE } from "@/lib/site";
import { buildBreadcrumb } from "@/lib/schema";
import JsonLd from "@/components/layout/JsonLd";
import Breadcrumbs from "@/components/layout/Breadcrumbs";

const ZONE_TAGS = [
    "Genève",
    "Plan-les-Ouates",
    "Carouge",
    "Lancy",
    "Vernier",
    "Meyrin",
    "Nyon",
    "Lausanne",
    "Vaud",
    "Fribourg",
    "Neuchâtel",
    "Jura",
    "Valais romand",
    "Suisse romande",
];

export default function ContactPage() {
    return (
        <>
            <Seo
                title="Contact RCPS Terrassement | Travaux à Genève et Suisse romande"
                description="Contactez RCPS Terrassement Sàrl à Plan-les-Ouates pour vos travaux de terrassement, démolition, génie civil, voirie, enrobés, aménagements extérieurs et enrochement à Genève et en Suisse romande."
            />
            <JsonLd
                data={buildBreadcrumb([
                    { name: "Accueil", path: "/" },
                    { name: "Contact", path: "/contact" },
                ])}
                id="breadcrumb-contact"
            />

            {/* Hero */}
            <section
                className="relative bg-navy-700 text-white overflow-hidden"
                data-testid="contact-hero"
            >
                <img
                    src="https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=2400&q=80"
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover opacity-40"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-navy-800/80 via-navy-700/70 to-navy-700" />
                <div className="absolute inset-0 bg-topo-dark opacity-40 mix-blend-overlay" />
                <div className="absolute inset-0 noise-overlay" />
                <div className="relative rcps-container pt-24 md:pt-28 pb-16 md:pb-24 grid md:grid-cols-12 gap-8 items-end">
                    <div className="md:col-span-8">
                        <Reveal>
                            <Breadcrumbs
                                variant="onImage"
                                trail={[
                                    { name: "Accueil", path: "/" },
                                    { name: "Contact", path: "/contact" },
                                ]}
                            />
                        </Reveal>
                        <Reveal delay={0.05}>
                            <p className="kicker text-white/80 mt-5">
                                <span className="hairline bg-white/60" /> Contact
                            </p>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <h1
                                className="mt-4 font-heading text-4xl md:text-6xl font-semibold leading-[1.05] tracking-tight"
                                data-testid="contact-h1"
                            >
                                Contactez RCPS Terrassement
                            </h1>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <p className="mt-6 text-white/85 max-w-2xl text-base md:text-lg leading-relaxed">
                                Vous avez un projet de terrassement, démolition, génie civil, voirie,
                                enrobés, aménagement extérieur ou enrochement à Genève ou en Suisse
                                romande ? Présentez-nous votre chantier, sa localisation, les accès
                                disponibles et les travaux souhaités.
                            </p>
                        </Reveal>
                        <Reveal delay={0.2}>
                            <p className="mt-4 text-white/75 max-w-2xl text-base md:text-lg leading-relaxed">
                                RCPS Terrassement Sàrl vous répondra après analyse de votre demande
                                afin de vous proposer une première orientation ou une offre adaptée.
                            </p>
                        </Reveal>
                    </div>
                    <div className="md:col-span-4 flex md:justify-end">
                        <Reveal delay={0.25}>
                            <a
                                href="#formulaire"
                                data-testid="hero-cta-devis"
                                className="inline-flex items-center gap-2 h-12 px-6 rounded-md bg-white text-navy-700 font-semibold hover:bg-concrete-50 transition"
                            >
                                Demander un devis <ArrowUpRight className="w-4 h-4" />
                            </a>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* Coordonnées + Horaires + Badges + Formulaire */}
            <section className="bg-white" id="formulaire" data-testid="contact-main">
                <div className="rcps-container py-16 md:py-24 grid md:grid-cols-12 gap-10 md:gap-14 items-start">
                    {/* Colonne coordonnées */}
                    <div className="md:col-span-5 lg:col-span-4">
                        <Reveal>
                            <p className="kicker text-navy-700">
                                <span className="hairline" /> Coordonnées
                            </p>
                            <h2 className="mt-3 font-heading text-2xl md:text-3xl font-semibold text-navy-700 tracking-tight">
                                Nous joindre directement
                            </h2>
                            <p className="mt-4 text-navy-700/80 text-sm md:text-base leading-relaxed">
                                Basée à Plan-les-Ouates, RCPS Terrassement Sàrl intervient à Genève,
                                dans l’ensemble du canton de Genève et dans toute la Suisse romande.
                                Vous pouvez nous contacter par téléphone, par email ou via le
                                formulaire de contact.
                            </p>

                            <ul className="mt-6 space-y-4 text-sm text-navy-700">
                                <li className="flex items-start gap-3" data-testid="contact-address">
                                    <MapPin className="w-4 h-4 mt-1 text-steel flex-shrink-0" />
                                    <span>
                                        <span className="font-semibold">RCPS Terrassement Sàrl</span>
                                        <br />
                                        {SITE.address.street}
                                        <br />
                                        {SITE.address.zip} {SITE.address.city}
                                        <br />
                                        Genève, {SITE.address.country}
                                    </span>
                                </li>
                                <li className="flex items-center gap-3">
                                    <Phone className="w-4 h-4 text-steel flex-shrink-0" />
                                    <a
                                        data-testid="contact-phone-link"
                                        href={`tel:${SITE.phoneTel}`}
                                        className="font-semibold hover:underline underline-offset-2"
                                    >
                                        {SITE.phone}
                                    </a>
                                </li>
                                <li className="flex items-center gap-3">
                                    <Phone className="w-4 h-4 text-steel flex-shrink-0" />
                                    <a
                                        data-testid="contact-phone2-link"
                                        href={`tel:${SITE.phone2Tel}`}
                                        className="font-semibold hover:underline underline-offset-2"
                                    >
                                        {SITE.phone2}
                                    </a>
                                </li>
                                <li className="flex items-center gap-3">
                                    <Mail className="w-4 h-4 text-steel flex-shrink-0" />
                                    <a
                                        data-testid="contact-email-link"
                                        href={`mailto:${SITE.email}`}
                                        className="font-semibold break-all hover:underline underline-offset-2"
                                    >
                                        {SITE.email}
                                    </a>
                                </li>
                            </ul>

                            <div className="mt-8 rounded-md overflow-hidden border border-concrete-200" data-testid="contact-visual">
                                <img
                                    src="/rcps-assets/visuel-page-contact.jpeg"
                                    alt="RCPS Terrassement — Genève et Suisse romande"
                                    className="w-full h-auto object-cover"
                                    loading="lazy"
                                />
                            </div>
                        </Reveal>
                    </div>

                    {/* Colonne formulaire */}
                    <div className="md:col-span-7 lg:col-span-8">
                        <Reveal delay={0.1}>
                            <p className="kicker text-navy-700">
                                <span className="hairline" /> Formulaire de contact
                            </p>
                            <h2 className="mt-3 font-heading text-2xl md:text-3xl font-semibold text-navy-700 tracking-tight">
                                Présentez-nous votre chantier
                            </h2>
                            <p className="mt-4 text-navy-700/80 text-sm md:text-base leading-relaxed">
                                Plus votre demande est précise, plus nous pouvons analyser
                                efficacement votre projet. Vous pouvez indiquer la localisation du
                                chantier, le type de travaux souhaités, les contraintes d’accès, les
                                surfaces ou volumes concernés, les délais envisagés et joindre des
                                photos ou documents si nécessaire.
                            </p>
                            <div className="mt-6">
                                <ContactForm />
                            </div>
                            <p className="mt-4 text-xs md:text-sm text-navy-700/60 leading-relaxed">
                                En envoyant ce formulaire, vous acceptez que RCPS Terrassement Sàrl
                                traite les données transmises afin de répondre à votre demande,
                                d’analyser votre projet et, le cas échéant, de préparer un devis. Les
                                informations et documents transmis sont traités conformément à notre{" "}
                                <a
                                    href={SITE.legal.privacy}
                                    target="_blank"
                                    rel="noreferrer noopener"
                                    className="text-navy-700 font-semibold underline underline-offset-2 hover:text-navy-800"
                                >
                                    Politique de confidentialité
                                </a>
                                .
                            </p>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* Zone d'intervention */}
            <section className="bg-white" data-testid="contact-zone">
                <div className="rcps-container py-16 md:py-24 grid md:grid-cols-12 gap-10 md:gap-16 items-start">
                    <div className="md:col-span-5">
                        <Reveal>
                            <p className="kicker text-navy-700">
                                <span className="hairline" /> Zone d’intervention
                            </p>
                            <h2 className="mt-4 font-heading text-3xl md:text-4xl font-semibold text-navy-700 tracking-tight leading-[1.15]">
                                À Genève et dans toute la Suisse romande
                            </h2>
                        </Reveal>
                    </div>
                    <div className="md:col-span-7 space-y-5 text-navy-700/80 leading-[1.8]">
                        <Reveal delay={0.08}>
                            <p>
                                RCPS Terrassement Sàrl intervient à Genève, dans l’ensemble du
                                canton de Genève et dans toute la Suisse romande pour les travaux de
                                terrassement, démolition, génie civil, voirie, enrobés, aménagements
                                extérieurs et enrochement.
                            </p>
                        </Reveal>
                        <Reveal delay={0.12}>
                            <p>
                                Nous étudions les demandes à Plan-les-Ouates, Genève, Carouge,
                                Lancy, Vernier, Meyrin, Onex, Nyon, Lausanne, dans le canton de
                                Vaud, à Fribourg, Neuchâtel, dans le Jura, en Valais romand et plus
                                largement en Suisse romande selon la nature des travaux, les accès
                                au chantier et les contraintes logistiques.
                            </p>
                        </Reveal>
                        <Reveal delay={0.16}>
                            <div className="mt-6 flex flex-wrap gap-2" data-testid="contact-zone-tags">
                                {ZONE_TAGS.map((z) => (
                                    <span
                                        key={z}
                                        className="px-3 py-1.5 rounded border border-concrete-200 text-navy-700 text-xs font-medium bg-white"
                                    >
                                        {z}
                                    </span>
                                ))}
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* Google Map */}
            <section className="bg-concrete-50 border-y border-concrete-200">
                <div className="rcps-container py-12 md:py-16">
                    <div className="rounded-md overflow-hidden border border-concrete-200 aspect-[16/8] relative">
                        <MapEmbed
                            address="Chemin du Pré-Fleuri 3, 1228 Plan-les-Ouates, Suisse"
                            label="Localisation RCPS Terrassement"
                            testId="contact-map"
                        />
                    </div>
                </div>
            </section>

            <PrestationsCrossLinks
                kicker="Nos prestations"
                title="Découvrez nos prestations avant d’envoyer votre demande."
                showRealisations={true}
            />

            {/* CTA final */}
            <SectionCTA
                title="Un projet à concrétiser ?"
                text="Présentez-nous votre chantier. Nous étudions votre demande de terrassement, démolition, génie civil, voirie, enrobés, aménagement extérieur ou enrochement à Genève ou en Suisse romande, puis revenons vers vous avec une proposition adaptée."
            />
        </>
    );
}
